/**
 * V10 Phase C — Edge Inspector 패널.
 *
 * ServiceDetailPage 의 Graph 탭 사이드 패널로 렌더. edge 클릭 시 열린다.
 *
 * 레이아웃 (§4.2):
 * ┌────────────────────────────────────────────┐
 * │ fromNode ──RELATION──→ toNode              │
 * │ Strength bar · Active/Invalidated 카운트   │
 * │ Edge status (VALID / INVALID)              │
 * ├────────────────────────────────────────────┤
 * │ Timeline chart (bar + strength curve)       │
 * ├────────────────────────────────────────────┤
 * │ Episode list (cursor 페이지네이션)          │
 * │ Semantic search within edge                 │
 * │ Attach new episode · Invalidate edge        │
 * └────────────────────────────────────────────┘
 *
 * URL 딥링크: `?edgeKey={fromId}|{RELATION}|{toId}&asOf=...`
 * asOf 변경 시 list 재조회 — `useAsOf` 훅 참조.
 */
import { useCallback, useEffect, useState } from "react"
import type {
	AdminClient,
	EpisodeListItem,
	ListEpisodesResponse,
	OntologyBlock
} from "../../../api/admin-client.ts"
import type { GraphNode } from "../../../components/graph/types.ts"
import { useAsOf } from "../../../hooks/useAsOf.tsx"
import { EdgeAttachEpisodeModal } from "./EdgeAttachEpisodeModal.tsx"
import { EdgeInvalidateLinkModal } from "./EdgeInvalidateLinkModal.tsx"
import { EdgeInvalidateModal } from "./EdgeInvalidateModal.tsx"
import { EdgeNodePill } from "./EdgeNodePill.tsx"
import { EpisodeInvalidateModal } from "./EpisodeInvalidateModal.tsx"
import { EpisodeListRow } from "./EpisodeListRow.tsx"
import { EpisodeTimelineChart } from "./EpisodeTimelineChart.tsx"
import { SemanticSearchWithinEdge } from "./SemanticSearchWithinEdge.tsx"

type Props = {
	readonly client: AdminClient
	readonly slug: string
	readonly selected: {
		readonly fromId: string
		readonly toId: string
		readonly relation: string
		readonly strength?: number
		readonly invalidAt?: string
	} | null
	/** edge 양쪽 노드 데이터 — 헤더에서 이름/kind/block icon 표시에 사용 */
	readonly fromNode: GraphNode | undefined
	readonly toNode: GraphNode | undefined
	readonly kindToBlock: ReadonlyMap<string, OntologyBlock>
	readonly onClose: () => void
	/** relation name → 한국어 displayName. EpisodeListRow 칩 번역에 사용 */
	readonly relationDisplayNames?: ReadonlyMap<string, string>
}

export function EdgeInspector(props: Props) {
	const { asOf } = useAsOf()
	const [data, setData] = useState<ListEpisodesResponse | undefined>()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [includeInvalidated, setIncludeInvalidated] = useState(false)
	const [attachOpen, setAttachOpen] = useState(false)
	const [edgeInvalidateOpen, setEdgeInvalidateOpen] = useState(false)
	const [linkInvalidateEpisode, setLinkInvalidateEpisode] = useState<EpisodeListItem | null>(null)
	const [episodeInvalidate, setEpisodeInvalidate] = useState<EpisodeListItem | null>(null)

	const refresh = useCallback(async () => {
		if (!props.selected) return
		setLoading(true)
		setError(undefined)
		try {
			const res = await props.client.listEpisodes(props.slug, {
				fromId: props.selected.fromId,
				toId: props.selected.toId,
				relation: props.selected.relation,
				...(asOf ? { asOf } : {}),
				includeInvalidated,
				limit: 50
			})
			setData(res)
		} catch (e) {
			setError((e as Error).message)
		} finally {
			setLoading(false)
		}
	}, [props.client, props.slug, props.selected, asOf, includeInvalidated])

	useEffect(() => {
		void refresh()
	}, [refresh])

	if (!props.selected) {
		return (
			<div
				style={{
					padding: "var(--pk-space-6)",
					color: "var(--pk-text-muted)",
					fontSize: "0.85rem",
					textAlign: "center"
				}}
			>
				그래프에서 edge 를 클릭하면 상세가 여기 열립니다.
			</div>
		)
	}

	const sel = props.selected
	const strength = data?.strength ?? sel.strength ?? 0
	const isInvalid = sel.invalidAt !== undefined

	return (
		<div style={{ padding: "var(--pk-space-4)", overflowY: "auto" }}>
			{/* Header — BlocksPage ShapeFlow 와 동일 비주얼. block icon + 이름 + relation pill. */}
			<div
				style={{
					display: "flex",
					alignItems: "flex-start",
					gap: "var(--pk-space-2)",
					marginBottom: "var(--pk-space-3)"
				}}
			>
				<div style={{ flex: 1, minWidth: 0 }}>
					{/* 좁은 Inspector 패널에서도 깨지지 않도록 세로 레이아웃 — 노드 pill 가득찬 너비 + 중앙 ↓. */}
					<div className="shape-flow shape-flow--vertical" style={{ margin: 0 }}>
						<EdgeNodePill
							node={props.fromNode}
							fallbackId={sel.fromId}
							kindToBlock={props.kindToBlock}
							size="md"
						/>
						<div className="shape-flow__rel">
							<span className="shape-flow__arrow shape-flow__arrow--down" aria-hidden>
								↓
							</span>
							<span className="shape-flow__rel-label">{sel.relation}</span>
							<span className="shape-flow__arrow shape-flow__arrow--down" aria-hidden>
								↓
							</span>
						</div>
						<EdgeNodePill
							node={props.toNode}
							fallbackId={sel.toId}
							kindToBlock={props.kindToBlock}
							size="md"
						/>
					</div>
				</div>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={props.onClose}
					aria-label="닫기"
					title="Inspector 닫기 (URL 의 ?edgeKey 파라미터도 제거)"
					style={{ padding: "4px 8px" }}
				>
					✕
				</button>
			</div>

			{/* Strength + counts */}
			<div
				style={{
					background: "var(--pk-bg-surface)",
					borderRadius: "var(--pk-radius-md)",
					padding: "var(--pk-space-3)",
					marginBottom: "var(--pk-space-3)"
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: "var(--pk-space-2)" }}>
					<span style={{ fontSize: "0.8rem", color: "var(--pk-text-muted)" }}>관계 강도</span>
					<div
						style={{
							flex: 1,
							height: 8,
							background: "var(--pk-gray-200)",
							borderRadius: "var(--pk-radius-pill)",
							overflow: "hidden"
						}}
					>
						<div
							style={{
								height: "100%",
								width: `${Math.round(strength * 100)}%`,
								background: "linear-gradient(90deg, var(--pk-brand-300), var(--pk-brand-500))",
								transition: "width 400ms ease"
							}}
						/>
					</div>
					<span
						style={{
							fontFamily: "var(--pk-font-mono)",
							fontSize: "0.8rem",
							color: "var(--pk-text-primary)"
						}}
					>
						{strength.toFixed(2)}
					</span>
				</div>
				<div
					style={{
						display: "flex",
						gap: "var(--pk-space-3)",
						marginTop: "var(--pk-space-2)",
						fontSize: "0.75rem",
						color: "var(--pk-text-muted)"
					}}
				>
					<span>활성 {data?.activeCount ?? 0}</span>
					<span>무력화 {data?.invalidatedCount ?? 0}</span>
					<span
						style={{
							marginLeft: "auto",
							padding: "1px 8px",
							borderRadius: "var(--pk-radius-pill)",
							background: isInvalid ? "var(--pk-danger-soft)" : "var(--pk-success-soft)",
							color: isInvalid ? "var(--pk-danger)" : "var(--pk-success)"
						}}
					>
						{isInvalid ? `종료 · ${new Date(sel.invalidAt ?? "").toLocaleDateString("ko-KR")}` : "유효"}
					</span>
				</div>
			</div>

			{/* asOf 힌트 */}
			{asOf && (
				<div
					style={{
						fontSize: "0.7rem",
						color: "var(--pk-text-muted)",
						marginBottom: "var(--pk-space-2)",
						fontStyle: "italic"
					}}
				>
					타임머신: {new Date(asOf).toLocaleString("ko-KR")} 시점 기준
				</div>
			)}

			{error && (
				<div className="alert alert-danger" style={{ marginBottom: "var(--pk-space-3)", fontSize: "0.8rem" }}>
					{error}
				</div>
			)}

			{/* Timeline chart */}
			<div style={{ marginBottom: "var(--pk-space-3)" }}>
				<EpisodeTimelineChart episodes={data?.episodes ?? []} strength={strength} />
			</div>

			{/* Episode list header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: "var(--pk-space-2)"
				}}
			>
				<h4 style={{ margin: 0, fontSize: "0.9rem" }}>근거 사건</h4>
				<label style={{ fontSize: "0.75rem", color: "var(--pk-text-muted)" }}>
					<input
						type="checkbox"
						checked={includeInvalidated}
						onChange={(e) => setIncludeInvalidated(e.target.checked)}
						style={{ marginRight: 4 }}
					/>
					무력화 포함
				</label>
			</div>

			{loading && (
				<div className="muted" style={{ fontSize: "0.8rem" }}>
					불러오는 중…
				</div>
			)}

			{data && data.episodes.length === 0 && !loading && (
				<div
					className="muted"
					style={{ fontSize: "0.8rem", textAlign: "center", padding: "var(--pk-space-4)" }}
				>
					이 edge 에 attach 된 episode 가 없습니다.
				</div>
			)}

			{data?.episodes.map((ep) => (
				<EpisodeListRow
					key={ep.episodeId}
					episode={ep}
					onInvalidateLink={() => setLinkInvalidateEpisode(ep)}
					onInvalidateEpisode={() => setEpisodeInvalidate(ep)}
					relationDisplayNames={props.relationDisplayNames}
				/>
			))}

			{/* Semantic search within this edge */}
			<SemanticSearchWithinEdge
				client={props.client}
				slug={props.slug}
				edgeKey={`${sel.fromId}|${sel.relation}|${sel.toId}`}
			/>

			{/* Actions */}
			<div
				style={{
					display: "flex",
					gap: "var(--pk-space-2)",
					marginTop: "var(--pk-space-5)",
					paddingTop: "var(--pk-space-3)",
					borderTop: "1px solid var(--pk-border-subtle)"
				}}
			>
				<button type="button" className="btn btn-primary btn-sm" onClick={() => setAttachOpen(true)}>
					+ 사건 첨부
				</button>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={() => setEdgeInvalidateOpen(true)}
					disabled={isInvalid}
					style={{ color: "var(--pk-danger)", marginLeft: "auto" }}
				>
					🚫 관계 종료
				</button>
			</div>

			{/* Modals */}
			{attachOpen && (
				<EdgeAttachEpisodeModal
					defaultEdge={{ fromId: sel.fromId, toId: sel.toId, relation: sel.relation }}
					onClose={() => setAttachOpen(false)}
					onSubmit={async (input) => {
						await props.client.attachEpisode(props.slug, input)
						await refresh()
					}}
				/>
			)}
			{edgeInvalidateOpen && (
				<EdgeInvalidateModal
					fromId={sel.fromId}
					toId={sel.toId}
					relation={sel.relation}
					onClose={() => setEdgeInvalidateOpen(false)}
					onConfirm={async (reason) => {
						await props.client.invalidateEdge(props.slug, {
							fromId: sel.fromId,
							toId: sel.toId,
							relation: sel.relation,
							reason
						})
						await refresh()
					}}
				/>
			)}
			{linkInvalidateEpisode && (
				<EdgeInvalidateLinkModal
					fromId={sel.fromId}
					toId={sel.toId}
					relation={sel.relation}
					episodeContent={linkInvalidateEpisode.content}
					onClose={() => setLinkInvalidateEpisode(null)}
					onConfirm={async (reason) => {
						await props.client.invalidateLink(props.slug, {
							episodeId: linkInvalidateEpisode.episodeId,
							fromId: sel.fromId,
							toId: sel.toId,
							relation: sel.relation,
							...(reason ? { reason } : {})
						})
						await refresh()
					}}
				/>
			)}
			{episodeInvalidate && (
				<EpisodeInvalidateModal
					episodeContent={episodeInvalidate.content}
					linkedEdgeCount={episodeInvalidate.otherLinkedEdges.length + 1}
					onClose={() => setEpisodeInvalidate(null)}
					onConfirm={async (reason) => {
						await props.client.invalidateEpisode(props.slug, episodeInvalidate.episodeId, reason)
						await refresh()
					}}
				/>
			)}
		</div>
	)
}
